"use client";

import React from "react";
import { ArrowRight, Binary, Cpu, Layers, SlidersHorizontal } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface GuideItem {
  id: string;
  icon: React.ComponentType<{
    className?: string;
    "aria-hidden"?: string | boolean;
  }>;
  title: Record<Lang, string>;
  description: Record<Lang, string>;
  to: string;
  category: Record<Lang, string>;
  specs: Record<Lang, string>;
}
const guides: GuideItem[] = [{
  id: "mesh",
  icon: SlidersHorizontal,
  title: {
    en: "Filter mesh selection guide",
    zh: "过滤丝网选型指南",
    es: "Guía de selección de malla filtrante",
    de: "Leitfaden Filtergewebeauswahl",
    fr: "Guide de sélection des toiles filtrantes",
    pt: "Guia de seleção de malhas filtrantes"
  },
  description: {
    en: "Compare square mesh and Dutch weave patterns to balance micron retention against flow resistance and operating pressure.",
    zh: "对比方孔网与荷兰编织结构，在微米级截留精度、流阻与工作压力之间实现最佳平衡。",
    es: "Compare malla cuadrada y tejido Dutch para equilibrar retención en micras y resistencia al flujo.",
    de: "Vergleichen Sie Quadratmaschen und Tressengewebe zur optimalen Abstimmung von Abscheidegrad und Durchfluss.",
    fr: "Comparez toiles carrées et toiles reps pour équilibrer rétention micronique et perte de charge.",
    pt: "Compare malha quadrada e tecelagem Dutch para equilibrar retenção de mícrons e resistência ao fluxo."
  },
  to: "/FilterMeshSelectionGuidePage",
  category: {
    en: "Woven Media",
    zh: "编织过滤介质",
    es: "Medios tejidos",
    de: "Drahtgewebe",
    fr: "Médias tissés",
    pt: "Meios tecidos"
  },
  specs: {
    en: "1 µm – 25 mm aperture",
    zh: "1 µm 至 25 mm 孔径",
    es: "Abertura 1 µm a 25 mm",
    de: "1 µm bis 25 mm Maschenweite",
    fr: "Ouverture 1 µm à 25 mm",
    pt: "Abertura 1 µm a 25 mm"
  }
}, {
  id: "wedge-wire",
  icon: Binary,
  title: {
    en: "Wedge wire screen selection guide",
    zh: "楔形滤网选型指南",
    es: "Guía de selección de tamices de alambre en cuña",
    de: "Leitfaden Spaltsiebauswahl",
    fr: "Guide de sélection des crépines à fil profilé",
    pt: "Guia de seleção de telas de arame em cunha"
  },
  description: {
    en: "Determine profile wire geometry, slot opening tolerances, and support rod spacing for continuous screening and backwashing.",
    zh: "确定型线几何结构、缝隙公差及支撑条间距，适用于连续筛分与反冲洗工况。",
    es: "Determine la geometría del alambre perfilado, tolerancias de ranura y espaciado de varillas de soporte.",
    de: "Bestimmen Sie Profildrahtgeometrie, Spaltweitentoleranzen und Tragstababstände für Siebung und Rückspülung.",
    fr: "Déterminez la géométrie du fil profilé, les tolérances d'écartement et l'espacement des tiges supports.",
    pt: "Determine a geometria do perfil, tolerâncias de fenda e espaçamento das hastes de suporte."
  },
  to: "/WedgeWireScreenSelectionGuidePage",
  category: {
    en: "Continuous Slot",
    zh: "连续缝隙结构",
    es: "Ranura continua",
    de: "Kontinuierlicher Spalt",
    fr: "Fente continue",
    pt: "Fenda contínua"
  },
  specs: {
    en: "0.02 – 5.0 mm slot",
    zh: "0.02 至 5.0 mm 缝隙",
    es: "Ranura 0.02 a 5.0 mm",
    de: "0,02 bis 5,0 mm Spaltweite",
    fr: "Fente 0,02 à 5,0 mm",
    pt: "Fenda 0,02 a 5,0 mm"
  }
}, {
  id: "sintered",
  icon: Layers,
  title: {
    en: "Sintered wire mesh selection guide",
    zh: "烧结丝网选型指南",
    es: "Guía de selección de malla sinterizada",
    de: "Leitfaden Sintergewebeauswahl",
    fr: "Guide de sélection des toiles frittées",
    pt: "Guia de seleção de malhas sinterizadas"
  },
  description: {
    en: "Evaluate multi-layer diffusion-bonded laminates for high-pressure reverse pulse cleaning, gas distribution, and polymer melt filtration.",
    zh: "评估用于高压反吹清洗、气体分布及聚合物熔体过滤的多层扩散固相烧结复合网。",
    es: "Evalúe laminados multicapa unidos por difusión para limpieza por pulso inverso y filtración de polímeros.",
    de: "Prüfen Sie mehrlagige diffusionsgeschweißte Laminate für Rückspülung, Gasverteilung und Polymerschmelzen.",
    fr: "Évaluez les laminés multicouches frittés pour le décolmatage sous pression et la filtration de polymères.",
    pt: "Avalie laminados multicamadas sinterizados por difusão para limpeza por pulso reverso e filtração."
  },
  to: "/SinteredWireMeshSelectionGuidePage",
  category: {
    en: "Diffusion Bonded",
    zh: "扩散固相烧结",
    es: "Unión por difusión",
    de: "Diffusionsverschweißt",
    fr: "Laminé fritté",
    pt: "Sinterizado por difusão"
  },
  specs: {
    en: "1 – 200 µm rating",
    zh: "1 至 200 µm 精度",
    es: "Clasificación 1 a 200 µm",
    de: "1 bis 200 µm Rating",
    fr: "Seuil 1 à 200 µm",
    pt: "Classificação 1 a 200 µm"
  }
}, {
  id: "components",
  icon: Cpu,
  title: {
    en: "Filter components selection guide",
    zh: "过滤组件选型指南",
    es: "Guía de selección de componentes de filtración",
    de: "Leitfaden Filterkomponentenauswahl",
    fr: "Guide de sélection des composants de filtration",
    pt: "Guia de seleção de componentes filtrantes"
  },
  description: {
    en: "Select custom fabricated baskets, pleated candle elements, stamped discs, and edge-reinforced cylinders built to your drawings.",
    zh: "根据图纸定制选型篮式滤筒、折叠烛式滤芯、冲压滤片及包边圆筒组件。",
    es: "Seleccione cestas a medida, elementos de vela plisados, discos estampados y cilindros reforzados.",
    de: "Wählen Sie maßgefertigte Filterkörbe, plissierte Kerzenfilter, Stanzronden und zylindrische Filterrohre.",
    fr: "Sélectionnez paniers sur mesure, éléments plissés, disques emboutis et cylindres renforcés.",
    pt: "Selecione cestos customizados, elementos vela pregueados, discos estampados e cilindros reforçados."
  },
  to: "/FilterComponentsSelectionGuidePage",
  category: {
    en: "Fabricated Elements",
    zh: "精密加工组件",
    es: "Elementos fabricados",
    de: "Konfektionierte Bauteile",
    fr: "Éléments confectionnés",
    pt: "Elementos fabricados"
  },
  specs: {
    en: "Custom OEM tooling",
    zh: "定制 OEM 工装模具",
    es: "Herramental OEM a medida",
    de: "Kundenspezifische OEM-Werkzeuge",
    fr: "Outillage OEM sur mesure",
    pt: "Ferramental OEM sob medida"
  }
}];
const SECTION_COPY = {
  eyebrow: {
    en: "Engineering Reference",
    zh: "工程技术参考",
    es: "Referencia técnica",
    de: "Technische Referenz",
    fr: "Référence technique",
    pt: "Referência técnica"
  },
  heading: {
    en: "Find the right specification for your process",
    zh: "为您的工艺流程匹配精准规格",
    es: "Encuentre la especificación precisa para su proceso",
    de: "Finden Sie die präzise Spezifikation für Ihren Prozess",
    fr: "Trouvez la spécification précise pour votre procédé",
    pt: "Encontre a especificação precisa para o seu processo"
  },
  lead: {
    en: "Step-by-step sizing parameters, alloy selection criteria, and mechanical load considerations for industrial screening and filtration media.",
    zh: "提供工业筛分与过滤介质的分步选型参数、合金材质选择标准及机械载荷计算指南。",
    es: "Parámetros de dimensionamiento paso a paso, criterios de aleación y consideraciones mecánicas para medios de filtración.",
    de: "Schrittweise Auslegungsparameter, Kriterien zur Legierungsauswahl und mechanische Faktoren für industrielle Filtrationsmedien.",
    fr: "Paramètres de dimensionnement pas à pas, critères de sélection des alliages et considérations mécaniques pour la filtration industrielle.",
    pt: "Parâmetros de dimensionamento passo a passo, critérios de liga e considerações mecânicas para meios de filtração industrial."
  },
  viewButton: {
    en: "View guide specifications",
    zh: "查看指南规格",
    es: "Ver especificaciones de la guía",
    de: "Leitfadenspezifikationen anzeigen",
    fr: "Voir les spécifications du guide",
    pt: "Ver especificações do guia"
  }
};
export default function SelectionGuides() {
  const {
    lang
  } = useLanguage();
  return <section id="selectionguidesgrid" data-nav="light" className="bg-background mf-section border-b border-border">
      <div className="mf-container">
        {/* Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 font-mono mf-caption font-semibold uppercase tracking-wide border-border bg-card">
            {pick(lang, SECTION_COPY.eyebrow)}
          </Badge>
          <h2 className="mf-h2 font-bold text-foreground tracking-tight mb-4 text-balance">
            {pick(lang, SECTION_COPY.heading)}
          </h2>
          <p className="mf-lead text-muted-foreground leading-relaxed">
            {pick(lang, SECTION_COPY.lead)}
          </p>
        </div>

        {/* Guides Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {guides.map((guide, i) => {
          const IconComponent = guide.icon;
          return <Reveal key={guide.id} delay={Math.min(i, 3) * 100} data-index={i}>
                <Card className="h-full flex flex-col justify-between border-border bg-card rounded-lg hover:border-primary/40 transition-colors duration-200">
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between gap-4">
                      {/* Standard card icon tile */}
                      <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </div>
                      <span className="font-mono mf-caption text-muted-foreground bg-muted px-2.5 py-1 rounded-sm border border-border">
                        {pick(lang, guide.specs)}
                      </span>
                    </div>
                    <div>
                      <span className="mf-caption font-medium text-primary uppercase tracking-wider block mb-1">
                        {pick(lang, guide.category)}
                      </span>
                      <CardTitle className="mf-h3 font-semibold text-card-foreground">
                        {pick(lang, guide.title)}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="pb-6">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, guide.description)}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={guide.to} className="flex items-center w-full justify-between">
                        <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                          {pick(lang, SECTION_COPY.viewButton)}
                        </span>
                        <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}